import axios from "axios";

import {
  getAccessToken,
  setAccessToken,
  stripTokensFromUserProfile,
} from "./SessionStore";
import { API_URLS } from "../Utils/Constants/APIConstants";
import {
  STATUS_CODES,
  API_ERRORS,
  ERROR_MESSAGES,
} from "../Utils/Constants/Constants";
import { setServerToday } from "../Utils/Helper/assessmentDateUtils";
import { getCsrfHeaders } from "../Utils/Helper/csrf";
import {
  getFromLocalStorage,
  saveToLocalStorage,
  STORAGE_KEYS,
} from "../Utils/Helper/LocalStorage";
import { clearLocalSessionState } from "../Utils/Helper/sessionCleanup";

const isAssessmentApi = url => {
  if (typeof url !== "string") return false;
  return (
    url.includes("/api/v1/assessments") ||
    url.includes("/api/v1/assessment-responses") ||
    url.includes("/api/v1/self-assessment-config") ||
    url.includes("/api/v1/vendor-config/disconnect_flow") ||
    url.includes("/api/v1/customers/accounts/assigned/portfolio") ||
    url.includes("/api/v1/executive/review-assessments")
  );
};

const applyServerToday = (url, headers) => {
  if (!isAssessmentApi(url)) return;
  const value = headers?.["x-server-today"];
  if (value) setServerToday(value);
};

// Single in-flight refresh shared by every caller (apiRequest 401 handling and
// SessionBootstrap). The backend can't tell concurrent refreshes apart, so we
// never fire more than one at a time.
let refreshTokenPromise = null;

const handleRefreshFailure = (redirectOnFailure = true) => {
  clearLocalSessionState();
  if (redirectOnFailure) {
    window.location.href = "/login";
  }
};

const isRefreshAuthFailure = (status, body) =>
  status === STATUS_CODES.UNAUTHORIZED ||
  status === STATUS_CODES.FORBIDDEN ||
  body?.error?.code === "AUTHENTICATION_FAILED";

const performTokenRefresh = async ({ redirectOnFailure = true } = {}) => {
  const loginData = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const tenantDomain = loginData?.tenantDomain || null;

  const headers = {
    "X-Tenant-Domain": tenantDomain,
    "Content-Type": "application/json",
    ...getCsrfHeaders(),
  };

  const tokenConfig = {
    method: "POST",
    url: API_URLS.refreshToken(),
    headers,
    withCredentials: true,
    data: {},
  };

  try {
    const { data: payload } = await axios(tokenConfig);
    const content = payload?.content ?? payload;

    if (
      isRefreshAuthFailure(null, payload) ||
      isRefreshAuthFailure(null, content)
    ) {
      handleRefreshFailure(redirectOnFailure);
      throw new Error("Token refresh failed");
    }

    if (content?.token?.access_token) {
      setAccessToken(content.token.access_token);
      if (loginData) {
        saveToLocalStorage(
          STORAGE_KEYS.LOGGED_IN_USER,
          stripTokensFromUserProfile(loginData)
        );
      }
      return true;
    }

    handleRefreshFailure(redirectOnFailure);
    throw new Error("Invalid token response");
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    const status = error?.response?.status;
    const body = error?.response?.data;

    if (error?.response && isRefreshAuthFailure(status, body)) {
      handleRefreshFailure(redirectOnFailure);
      throw new Error("Token refresh failed");
    }

    throw error;
  }
};

export const refreshAccessToken = (options = {}) => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = performTokenRefresh(options).finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

const apiRequest = async (
  method,
  url,
  data = null,
  encode = false,
  isFileUpload = false,
  options = {}
) => {
  const loginData = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const tenantDomain = loginData?.tenantDomain || null;
  const accessToken = getAccessToken();

  try {
    const headers = {
      ...getCsrfHeaders(),
    };

    if (tenantDomain) {
      headers["X-Tenant-Domain"] = tenantDomain;
    }

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    // Store original data for potential retry
    let requestData = data;
    if (encode) {
      headers["Content-Type"] = "application/x-www-form-urlencoded";
      requestData = new URLSearchParams(data).toString();
    } else if (isFileUpload && data instanceof FormData) {
      // For file uploads, let axios set the Content-Type automatically
      // Don't set Content-Type header for FormData
      requestData = data;
    } else if (!isFileUpload) {
      headers["Content-Type"] = "application/json";
    }

    // Caller-supplied headers merge last so a caller can add request-scoped
    // context (the Data Hub's active dataset, for one) without every helper
    // growing a parameter for it. Content-Type is set above and deliberately
    // overridable — a caller that knows better should win.
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    const config = {
      method,
      url,
      headers,
      data: requestData,
      withCredentials: true,
    };
    if (options.signal) {
      config.signal = options.signal;
    }
    if (options.responseType) {
      config.responseType = options.responseType;
    }
    if (options.onUploadProgress) {
      config.onUploadProgress = options.onUploadProgress;
    }

    const response = await axios(config);
    applyServerToday(url, response?.headers);
    if (options.returnFullResponse) {
      return response;
    }
    return response.data;
  } catch (error) {
    // Re-throw cancellation so callers can treat it as aborted (e.g. ignore in reducer)
    if (axios.isCancel(error)) {
      throw error;
    }
    applyServerToday(url, error?.response?.headers);
    if (options.returnFullResponse && error?.response) {
      throw error;
    }
    if (error?.response) {
      const { status, data: errorData } = error.response;

      if (status === STATUS_CODES.UNAUTHORIZED && !options._retried) {
        // Another request may already have refreshed the token while this one
        // was in flight; only refresh if the token we sent is still current.
        if (getAccessToken() === accessToken) {
          try {
            await refreshAccessToken();
          } catch (refreshError) {
            if (axios.isCancel(refreshError)) {
              throw refreshError;
            }
            throw new Error(
              JSON.stringify({
                status: STATUS_CODES.UNAUTHORIZED,
                message: "Token refresh failed",
              })
            );
          }
        }
        // Retry once with the new token; a second 401 surfaces as an error.
        return apiRequest(method, url, data, encode, isFileUpload, {
          ...options,
          _retried: true,
        });
      } else {
        const errorContent = errorData?.content;
        throw new Error(
          JSON.stringify({
            status,
            message:
              errorData?.response?.message ||
              errorData?.message ||
              errorData?.detail ||
              error.message,
            code: errorContent?.code,
            content: errorContent,
          })
        );
      }
    } else if (error.request) {
      throw new Error(
        JSON.stringify({
          status: API_ERRORS.NO_RESPONSE,
          message: ERROR_MESSAGES.NO_RESPONSE_FROM_SERVER,
        })
      );
    } else {
      throw new Error(
        JSON.stringify({
          status: API_ERRORS.REQUEST_SETUP_ERROR,
          message: error.message,
        })
      );
    }
  }
};

export const get = (url, options) =>
  apiRequest("GET", url, null, false, false, options);
export const post = (url, data, encode, options) =>
  apiRequest("POST", url, data, encode, false, options);
export const put = (url, data) => apiRequest("PUT", url, data);
/** @type {(url: string, data?: any) => Promise<any>} */
export const del = (url, data = null) => apiRequest("DELETE", url, data);

// Special function for file uploads
// `fields`: optional extra multipart fields sent next to the file ({name: value}).
export const uploadFile = async (url, file, options, fields) => {
  const formData = new FormData();
  formData.append("file", file);
  if (fields) {
    Object.entries(fields).forEach(([name, value]) => {
      if (value !== undefined && value !== null) formData.append(name, value);
    });
  }

  return apiRequest("POST", url, formData, false, true, options);
};
export const patch = (url, data) => apiRequest("PATCH", url, data);

export default apiRequest;
